/**
 * The org row at the top of the sidebar (SPEC decision 22, plan P22). With one org and no invitation it only names it;
 * otherwise it is a menu of the user's orgs (`GET /me` → `orgs`), where choosing one switches the whole console to it,
 * followed by the invitations to orgs the user is not in yet (`pendingInvitations`), each joined with one click.
 * During a client preview it names the client and offers nothing: the banner is the way out.
 */
import type { Me } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { joinLabel, pendingInvitationsOf, switcherOrgs } from './shellModel';
import { useOrgActions } from './useOrgActions';

const ROW = 'flex h-10 w-full items-center gap-2.5 rounded-lg border bg-secondary px-2.5 text-left';

function OrgInitial({ name, size, dot }: { name: string; size: 6 | 7; dot?: boolean }) {
  return (
    <span className={cn('relative flex shrink-0 items-center justify-center rounded-md bg-primary/15 font-mono font-semibold text-primary', size === 6 ? 'size-6 text-[11px]' : 'size-7 text-xs')}>
      {name.slice(0, 1).toUpperCase()}
      {dot ? <span data-testid="org-invitations-dot" className="absolute -right-1 -top-1 size-2.5 rounded-full border-2 border-secondary bg-primary" /> : null}
    </span>
  );
}

export function OrgSwitcher({ me, rail }: { me: Me | undefined; rail: boolean }) {
  const { switchOrg, joinOrg } = useOrgActions();
  if (!me) return rail ? <div className="flex h-10 items-center justify-center"><Skeleton className="size-7" /></div> : <div className={ROW}><Skeleton className="size-6" /><Skeleton className="h-3.5 w-20" /></div>;
  const orgs = switcherOrgs(me);
  const invitations = pendingInvitationsOf(me);
  const face = rail ? (
    <span className="flex h-10 w-full items-center justify-center"><OrgInitial name={me.org.name} size={7} dot={invitations.length > 0} /></span>
  ) : (
    <>
      <OrgInitial name={me.org.name} size={6} dot={invitations.length > 0} />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{me.org.name}</span>
      {orgs.length ? <Icon name="unfold_more" size={18} className="text-muted-foreground" /> : null}
    </>
  );
  if (!orgs.length) return <div className={rail ? undefined : ROW} title={me.org.name} data-testid="org-row">{face}</div>;
  const invited = invitations.length ? `, ${invitations.length} invitation${invitations.length > 1 ? 's' : ''}` : '';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="org-switcher" aria-label={`Organization: ${me.org.name}${invited}. Switch organization`} title={rail ? me.org.name : undefined} className={cn(rail ? 'rounded-lg hover:bg-accent' : cn(ROW, 'transition-colors hover:bg-accent'), 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}>
          {face}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={rail ? 'right' : 'bottom'} className="min-w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Switch organization</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={me.org.id} onValueChange={(id) => id !== me.org.id && switchOrg(id)}>
          {orgs.map((o) => (
            <DropdownMenuRadioItem key={o.id} value={o.id} data-testid={`org-option-${o.slug}`} className="min-h-11 sm:min-h-9">
              <span className="truncate">{o.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {invitations.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Invitations</DropdownMenuLabel>
            {invitations.map((p) => (
              <DropdownMenuItem key={p.id} data-testid={`org-join-${p.org.slug}`} onSelect={() => void joinOrg(p.id)} className="min-h-11 sm:min-h-9">
                <Icon name="person_add" size={18} className="text-primary" />
                <span className="truncate">{joinLabel(p)}</span>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
